import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { AuthService } from 'src/app/public/services/auth.service';
import { JWTTokenServiceService } from 'src/app/public/services/token.service';
import { UsersService } from 'src/app/private/services/users.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

/** How often (ms) to ping the backend and verify the session is still active. */
const POLL_INTERVAL_MS = 30_000;

/**
 * Polls `validateSession` every 30 s (and on window focus) to detect when the
 * same account logs in from a different device. When invalidation is detected the
 * service clears the local session and redirects to the login page.
 *
 * Start polling by calling `startPolling()` right after a successful login.
 * Stop it by calling `stopPolling()` during explicit logout or in `ngOnDestroy`.
 */
@Injectable({ providedIn: 'root' })
export class SessionGuardService implements OnDestroy {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private focusSub: Subscription | null = null;

  constructor(
    private authService: AuthService,
    private tokenService: JWTTokenServiceService,
    private localStorageService: LocalStorageService,
    private usersService: UsersService,
    private router: Router,
    private ngZone: NgZone,
  ) {}

  startPolling(): void {
    this.stopPolling();

    // Run the check outside Angular's change-detection zone to avoid unnecessary CD cycles.
    this.ngZone.runOutsideAngular(() => {
      this.intervalId = setInterval(() => this.check(), POLL_INTERVAL_MS);

      // Also check immediately when the tab becomes visible / the window regains focus.
      this.focusSub = fromEvent(window, 'focus')
        .pipe(debounceTime(500))
        .subscribe(() => this.check());
    });
  }

  stopPolling(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.focusSub?.unsubscribe();
    this.focusSub = null;
  }

  private check(): void {
    const token = this.tokenService.getToken();
    if (!token) {
      return;
    }

    this.authService.validateSession().subscribe({
      next: () => { /* session still valid — no action needed */ },
      error: (err) => {
        const code = err?.error?.code;
        if (
          code === 'SESSION_INVALIDATED' ||
          code === 'TOKEN_EXPIRED' ||
          code === 'ACCOUNT_INACTIVE'
        ) {
          this.ngZone.run(() => this.forceLogout());
        }
      },
    });
  }

  private forceLogout(): void {
    this.stopPolling();
    this.tokenService.setToken('');
    this.usersService.clearFindAllUsersInflight();
    this.localStorageService.logout();
    this.router.navigate(['/login'], { queryParams: { reason: 'session_invalidated' } });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}
